import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SEO, PAGE_SEO } from "@/components/SEO";

const sections = [
  {
    title: "1. Introduction and Acceptance",
    content: `This Privacy Policy explains how Lifely Holdings Limited (NZBN 9429053450883) ("Lifely", "we", "us") collects, uses, discloses, and protects personal information when you use lifely.guide and app.lifely.guide (the "Service").

By registering or using the Service, you agree to this Privacy Policy. If you do not agree, do not use the Service.`,
  },
  {
    title: "2. Personal Information We Collect",
    content: `We practice data minimisation. The only personal information we intentionally collect and store is:

• Name
• Email address
• Phone number (if you provide it)

We do not collect or store payment card numbers. Payments are processed by Stripe.`,
  },
  {
    title: "3. User-Entered Content (You Control What You Enter)",
    content: `The Service enables you to enter notes and organisational information. This may include sensitive information if you choose to enter it.

You are responsible for what you enter. We do not monitor or validate user-entered content. Do not enter private keys, seed phrases, master passwords, or highly sensitive secrets.`,
  },
  {
    title: "4. How We Use Personal Information",
    content: `• To create and administer your account
• To provide Service communications (e.g., confirmations, security notices)
• To provide support and respond to requests
• To administer subscriptions via Memberstack and process payments via Stripe
• To comply with legal obligations and enforce our Terms

We do not sell your personal information.`,
  },
  {
    title: "5. Service Providers and Disclosures",
    content: `We use third-party providers to operate the Service:

• Cloudflare — hosting and delivery infrastructure
• Memberstack — authentication and subscription management
• Stripe — payment processing

We may disclose personal information if required by law, to protect rights and safety, or in connection with a corporate transaction (e.g., sale of assets), subject to appropriate safeguards.`,
  },
  {
    title: "6. Security Measures and Limitations",
    content: `We take reasonable technical and organisational steps to protect personal information. However, no system is perfectly secure. Software may contain bugs and security incidents may occur.

No guarantee of absolute security. You acknowledge and accept that transmission and storage of data carry inherent risks. We are not responsible for security failures outside our reasonable control (including third-party breaches).`,
  },
  {
    title: "7. Data Security & Encryption Tiers",
    content: `Standard Plan (Managed Security):
For our standard continuity and asset mapping services, data is encrypted during transit and at rest using industry-standard AES-256 encryption. This allows our system to facilitate automated check-ins and notify your nominees as per your instructions. Data is stored on our managed cloud infrastructure (Cloudflare D1).

Plus Plan (Enhanced Security):
Plus Plan security features will be detailed upon launch. The Plus Plan will offer enhanced security options for users requiring additional protection for sensitive information.`,
  },
  {
    title: "8. International Transfers",
    content: `We operate from New Zealand. Our providers may process data in other jurisdictions (including the United States and other locations). By using the Service, you consent to cross-border transfers as necessary to provide the Service.`,
  },
  {
    title: "9. GDPR Notice for EEA/UK Users",
    content: `If you are in the EEA or UK, Lifely is generally the "controller" of your personal information we collect (name, email, phone).

Lawful bases include: (a) performance of a contract (providing the Service), (b) legitimate interests (operating and securing the Service), and (c) compliance with legal obligations.

Your rights may include: access, rectification, erasure, restriction, portability, objection, and the right to lodge a complaint with a supervisory authority.

International transfers: Your data may be processed outside the EEA/UK. We rely on appropriate safeguards offered by our providers where available and on necessity to perform our contract with you.

Automated decision-making: We do not conduct profiling or automated decision-making with legal or similarly significant effects using your PII.`,
  },
  {
    title: "10. CCPA/CPRA Notice for California Residents",
    content: `If you are a California resident, you may have rights to: (a) know what personal information we collect, use, and disclose; (b) request deletion; (c) correct inaccurate information; and (d) not be discriminated against for exercising rights.

Categories collected: identifiers (name, email, phone).

Selling/sharing: We do not sell personal information and do not "share" it for cross-context behavioural advertising.

To exercise requests, contact: privacy@lifely.guide. We may verify your request and may deny requests where permitted by law.`,
  },
  {
    title: "11. Retention, Access, and Deletion",
    content: `We retain personal information while your account is active and as required for legitimate business or legal purposes.

You may request a copy of your personal information or deletion of your account. We will respond within a reasonable time and in accordance with applicable law.

Backups. Deleted data may persist in encrypted backups for a limited period and will be overwritten on normal cycles, where feasible.`,
  },
  {
    title: "12. Cookies and Analytics",
    content: `We may use essential cookies required for login and security. If we add analytics in future, we will update this policy and, where required, provide consent mechanisms.`,
  },
  {
    title: "13. Children",
    content: `The Service is not intended for children under 18. We do not knowingly collect personal information from children.`,
  },
  {
    title: "14. Changes and Contact",
    content: `We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors. When we make changes, we will update the "Effective date" at the top of this policy. Changes apply when posted.

We encourage you to review this policy periodically. Your continued use of the Service after changes are posted constitutes your acceptance of the updated policy.

Governing law: New Zealand, subject to non-excludable rights under applicable law.`,
  },
];

export default function Privacy() {
  return (
    <div className="pt-24">
      <SEO {...PAGE_SEO.privacy} />
      <section className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-6">
          <Breadcrumb className="mb-8">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Privacy Policy</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4">
              Legal
            </p>
            <h1 className="font-serif text-4xl md:text-5xl text-foreground">
              Privacy Policy
            </h1>
            <p className="mt-4 text-muted-foreground">
              Lifely Holdings Limited (NZBN 9429053450883)
            </p>
            <p className="mt-1 text-muted-foreground">
              Effective date: 28 February 2026
            </p>
            <p className="mt-1 text-sm text-muted-foreground/70">
              Websites: lifely.guide / app.lifely.guide
            </p>
          </motion.div>

          <div className="mt-14 space-y-10">
            {sections.map((section, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-lg font-semibold text-foreground mb-3">
                  {section.title}
                </h2>
                <div className="text-muted-foreground leading-[1.8] whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 pt-8 border-t border-border/50">
            <p className="text-sm text-muted-foreground/70">
              Questions about this policy? Contact us at{" "}
              <a
                href="mailto:privacy@lifely.guide"
                className="text-primary hover:underline"
              >
                privacy@lifely.guide
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
